import { getAiSettingsUrl } from 'ai-studio-lib/shared/utils/urls';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { default as FormattedJSXMessage, default as FormattedMessage } from 'I18n/components/FormattedJSXMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { useMemo } from 'react';
import { useGetSatisfyingOptionsQuery } from '../../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useInstallability } from '../../hooks/useInstallability';
import { getAiControlTypeText } from '../../utils/aiAccessControlsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function useGetInstallability({
  listingId,
  published,
  appContext = 'app',
  productType
}) {
  var _installabilityData$i, _installabilityData$i2, _installabilityData$i3, _installabilityData$i4, _installabilityData$i5, _installabilityData$i6, _installabilityData$i7, _installabilityData$i8, _installabilityData$i9, _installabilityData$i10, _installabilityData$i11, _installabilityData$i12, _installabilityData$i13, _installabilityData$i14, _installabilityData$i15;
  const {
    trackInteraction
  } = useDetailsPageContext();
  const {
    data: installabilityData,
    loading: installabilityLoading,
    refetch: refetchInstallability
  } = useInstallability({
    listingId,
    published,
    appContext
  });
  const OpenAISettingsLink = useMemo(() => ({
    children
  }) => {
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: getAiSettingsUrl(),
      onClick: () => {
        trackInteraction({
          action: 'click ai settings link'
        });
      },
      use: "on-dark",
      whiteSpace: "nowrap",
      children: children
    });
  }, [trackInteraction]);
  const missingUserScopes = (_installabilityData$i = installabilityData === null || installabilityData === void 0 || (_installabilityData$i2 = installabilityData.installability) === null || _installabilityData$i2 === void 0 || (_installabilityData$i2 = _installabilityData$i2.uninstallableReason) === null || _installabilityData$i2 === void 0 ? void 0 : _installabilityData$i2.missingUserScopes) !== null && _installabilityData$i !== void 0 ? _installabilityData$i : [];
  const missingPortalScopes = (_installabilityData$i3 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i4 = installabilityData.installability) === null || _installabilityData$i4 === void 0 || (_installabilityData$i4 = _installabilityData$i4.uninstallableReason) === null || _installabilityData$i4 === void 0 ? void 0 : _installabilityData$i4.missingPortalScopes) !== null && _installabilityData$i3 !== void 0 ? _installabilityData$i3 : [];
  const productSpecific = installabilityData === null || installabilityData === void 0 || (_installabilityData$i5 = installabilityData.installability) === null || _installabilityData$i5 === void 0 || (_installabilityData$i5 = _installabilityData$i5.uninstallableReason) === null || _installabilityData$i5 === void 0 ? void 0 : _installabilityData$i5.productSpecificUninstallableReason;
  const missingAiControls = (productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific['@type']) === 'agent' ? productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.missingAiAccessControls : undefined;
  const allMissingScopes = [...missingUserScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...missingPortalScopes.map(scope => scope.toLowerCase().replaceAll('_', '-'))];
  const {
    data: missingPlanData,
    loading: missingPlanDataLoading
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: allMissingScopes
    },
    skip: allMissingScopes.length === 0
  });
  const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
  const missingPlanApiName = useMemo(() => {
    if ((missingPlanOptions === null || missingPlanOptions === void 0 ? void 0 : missingPlanOptions.length) === 1 && missingPlanOptions[0].and.length === 1) {
      const option = missingPlanOptions[0].and[0].or[0];
      return option === null || option === void 0 ? void 0 : option.productApiName;
    }
    return undefined;
  }, [missingPlanOptions]);
  const missingPlanTooltip = useMemo(() => {
    if (missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      }
      return /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
          options: {
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        }), /*#__PURE__*/_jsx(List, {
          marginBlockStart: "200",
          marginInlineStart: "-400",
          styled: true,
          children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
              options: {
                planName: option.displayName
              }
            })
          }, index))
        })]
      });
    } else if (missingAiControls && missingAiControls.length > 0) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.aiAccessControls.description_jsx",
        elements: {
          Link: OpenAISettingsLink
        },
        options: {
          settings: missingAiControls.map(control => getAiControlTypeText(control)).join(', ')
        }
      });
    }
  }, [missingPlanOptions, missingAiControls, productType, OpenAISettingsLink]);

  // this will be true if the product is uninstallable only because the user is not in the public beta
  const isOnlyMissingPublicBetaAccessForInstall = (!(installabilityData !== null && installabilityData !== void 0 && (_installabilityData$i6 = installabilityData.installability) !== null && _installabilityData$i6 !== void 0 && _installabilityData$i6.userInstallable) || !(installabilityData !== null && installabilityData !== void 0 && (_installabilityData$i7 = installabilityData.installability) !== null && _installabilityData$i7 !== void 0 && _installabilityData$i7.portalInstallable)) && allMissingScopes.length === 0 && (installabilityData === null || installabilityData === void 0 || (_installabilityData$i8 = installabilityData.installability) === null || _installabilityData$i8 === void 0 || (_installabilityData$i8 = _installabilityData$i8.uninstallableReason) === null || _installabilityData$i8 === void 0 ? void 0 : _installabilityData$i8.productSpecificUninstallableReason) === undefined && (installabilityData === null || installabilityData === void 0 || (_installabilityData$i9 = installabilityData.installability) === null || _installabilityData$i9 === void 0 || (_installabilityData$i9 = _installabilityData$i9.uninstallableReason) === null || _installabilityData$i9 === void 0 ? void 0 : _installabilityData$i9.betaListingGate) !== undefined;
  return {
    loading: installabilityLoading || allMissingScopes.length > 0 && missingPlanDataLoading,
    installed: (_installabilityData$i10 = installabilityData === null || installabilityData === void 0 ? void 0 : installabilityData.installed) !== null && _installabilityData$i10 !== void 0 ? _installabilityData$i10 : false,
    missingScopes: allMissingScopes,
    userInstallable: (_installabilityData$i11 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i12 = installabilityData.installability) === null || _installabilityData$i12 === void 0 ? void 0 : _installabilityData$i12.userInstallable) !== null && _installabilityData$i11 !== void 0 ? _installabilityData$i11 : false,
    portalInstallable: (_installabilityData$i13 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i14 = installabilityData.installability) === null || _installabilityData$i14 === void 0 ? void 0 : _installabilityData$i14.portalInstallable) !== null && _installabilityData$i13 !== void 0 ? _installabilityData$i13 : false,
    uninstallableReason: installabilityData === null || installabilityData === void 0 || (_installabilityData$i15 = installabilityData.installability) === null || _installabilityData$i15 === void 0 ? void 0 : _installabilityData$i15.uninstallableReason,
    missingPlanData,
    missingPlanTooltip,
    //TODO: Rename to something generic? Not just referring to missing plan now
    missingPlanApiName,
    isOnlyMissingPublicBetaAccessForInstall,
    refetchInstallability
  };
}